import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useCardsStore } from "@stores/survey/cards";
import { useSurveySectionsStore } from "@stores/survey/surveySections";
import { useSectionsStore } from "@stores/survey/sections";
import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
import { useConstantsStore } from "@stores/constants";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useSurveyStore } from "@stores/survey/survey";
import useMyFetch from "@stores/useMyFetch";
import { addCardPayload, duplicateCardPayload } from "@stores/survey/cardPayloadHandler";
import { updateLoopTokens, deleteObsoleteConditions } from "@stores/survey/computeCardsList";
import { updateAllDynamicAnswersAndItems } from "@utils/composables/updateAllDynamicAnswersAndItems";
import { inferGroupTypeFromPrefix } from "@stores/survey/prepareCards";
import { isEmpty } from "@utils/toolBox";
import { RECODE_KIND_DEFAULTS, recodeKindFromTypeKey } from "@utils/recodeKinds";
import { createAnswerItem, createRangeItem } from "@stores/survey/cardPayloadHandler";
import { useResultsV2RecodesStore } from "@stores/survey/resultsV2/resultsV2.recodes";

export const useSurveyCardsStore = defineStore("surveyCards", () => {
  const cardsStore = useCardsStore();
  const currentCardStore = useCurrentCardStore();
  const sectionsStore = useSurveySectionsStore();
  const localesStore = useSurveyLocalesStore();
  const constantsStore = useConstantsStore();
  const surveyStore = useSurveyStore();
  const { expandSectionIncludingCard } = sectionsStore;
  const { cardSubTypesInfos, cardTypesGroups, cardSubTypes } = storeToRefs(constantsStore);

  // state
  const searchQuery = ref("");
  const switchBetweenTabs = ref(false);
  const firstLoadReviewPage = ref(false);
  const activePane = ref("Edit");
  const startFromCardToken = ref("");

  // getters
  const cards = computed(() => cardsStore.cards);
  const isFilterActive = computed(() => !isEmpty(searchQuery.value));
  const questionCount = computed(() => {
    const questionsList = cards.value.filter((c) => c.type.startsWith("q_") && c.type !== "q_hidden");
    return questionsList.length;
  });
  const presenceOfCard = computed(() => {
    const cardsList = cards.value.filter((card) => !card.token.includes("_gen"));
    return !isEmpty(cardsList);
  });

  // actions
  const groupTypeOf = (type) => constantsStore.getCardTypeGroupName(type) || inferGroupTypeFromPrefix(type);

  const defaultCardName = (type, startIndex) => {
    let index = Math.max(startIndex, 0);
    while (cards.value.some((card) => card.name === `${type}_${index}`)) index++;
    return `${type}_${index}`;
  }

  const insertCards = (elementsToAdd, afterToken) => {
    let previous = afterToken;
    elementsToAdd.forEach((card) => {
      const index = previous ? cardsStore.findIndex(previous) + 1 : cards.value.length;
      if (!card.name) card.name = defaultCardName(card.type, index);
      card.groupType = groupTypeOf(card.type);
      cardsStore.addCard(card, previous);
      previous = card.token;
    });
    updateLoopTokens(cards.value);
  }

  // A "recode:<kind>" menu key is a q_hidden card preset for that kind.
  const cardTypeFor = (typeKey) => (recodeKindFromTypeKey(typeKey) ? "q_hidden" : typeKey);

  const recodePreset = (card, kind) => {
    const defaults = RECODE_KIND_DEFAULTS[kind];
    if (!defaults) return card;
    useResultsV2RecodesStore().initNewRecode(card.token, kind, { surface: "programming" });
    card.recode_kind = kind;
    card.is_for_analytics = false;
    card.specs = {
      ...card.specs,
      recode_type: defaults.recodeType,
      transformation_type: defaults.transformationType,
      answers: [],
      ranges: [],
    };
    if (kind === "conditions") {
      const answer = createAnswerItem({});
      card.specs.answers = [answer];
      card.specs.ranges = [{ ...createRangeItem("conditions"), to: answer.code }];
    }
    return card;
  };

  const standardCardsPayload = async (cardType) => {
    const { data } = await useMyFetch(`surveys/${surveyStore.token}/standard_cards?type=${cardType}`).json();
    return data.value ?? [];
  };

  const newCardsPayload = async (typeKey) => {
    const cardType = cardTypeFor(typeKey);
    if (!cardSubTypes.value.includes(cardType)) return [];
    if (cardType.startsWith("s_")) return standardCardsPayload(cardType);
    return addCardPayload(cardType).map((card) => recodePreset(card, recodeKindFromTypeKey(typeKey)));
  };

  const addCard = async ({ token, type }) => {
    const elementsToAdd = await newCardsPayload(type);
    if (isEmpty(elementsToAdd)) return;
    insertCards(elementsToAdd, token);
    selectCurrentCard({ token: elementsToAdd[0].token });
  }

  const addCardAtTheEnd = async ({ type }) => {
    const elementsToAdd = await newCardsPayload(type);
    if (isEmpty(elementsToAdd)) return;
    insertCards(elementsToAdd, cards.value.at(-1)?.token ?? null);
    selectCurrentCard({ token: elementsToAdd[0].token });
  }

  const selectConditions = ({ token }) => {
    selectCurrentCard({ token, selectConditions: true });
  }

  const selectCurrentCard = ({ token, selectConditions = false }) => {
    clearSearchQuery();
    expandSectionIncludingCard(token);
    const index = cardsStore.findIndex(token);
    const cardToSelect = cardsStore.findByToken(token);
    if (!cardToSelect) return;
    cardsStore.selectedToken = token;
    currentCardStore.selectCard({ card: cardToSelect, index, selectConditions });
  }

  const hasUnsavedChanges = computed(() => cardsStore.hasUnsavedChanges || useSectionsStore().hasUnsavedChanges);

  const refreshCards = async () => {
    if (hasUnsavedChanges.value) return;
    await cardsStore.fetch();
    await useSectionsStore().fetch();
    const token = cardsStore.selectedToken;
    if (token && cardsStore.findByToken(token)) selectCurrentCard({ token });
    else selectFirstCardAsCurrent();
  }

  const isSelectableCard = (card) =>
    !card.token.match("_gen_") && !card.token.match("_ai_chat_quality_") && !card.is_for_analytics;

  const selectFirstCardAsCurrent = () => {
    const firstCard = cards.value.find(isSelectableCard);
    if (firstCard == null) return false;
    selectCurrentCard({ token: firstCard.token });
  }

  const removeCard = ({ token }) => {
    const selectableCards = cards.value.filter((c) => c.is_for_analytics !== true);
    const selectableIndex = selectableCards.findIndex((c) => c.token === token);
    const indexToSelect = selectableIndex + 1 === selectableCards.length ? selectableIndex - 1 : selectableIndex + 1;
    const cardToSelect = selectableCards.at(indexToSelect);

    cardsStore.removeCard(token);
    deleteObsoleteConditions(cards.value, token);
    updateLoopTokens(cards.value);
    if (cardToSelect && cardToSelect.token !== token) selectCurrentCard({ token: cardToSelect.token });
  }

  const duplicateCard = ({ token }) => {
    const cardToDuplicate = cardsStore.findByToken(token);
    if (!cardToDuplicate) return;
    const duplicatedCard = duplicateCardPayload(cardToDuplicate);
    cardsStore.addCard(duplicatedCard, token);
    updateLoopTokens(cards.value);
    selectCurrentCard({ token: duplicatedCard.token });
  }

  const duplicateCards = ({ tokens }) => {
    const ordered = cards.value.filter(({ token }) => tokens.includes(token));
    if (!ordered.length) return;

    const anchorCard = ordered.at(-1);
    let anchorToken = anchorCard.token;
    let lastCopyToken = null;

    ordered.forEach(({ token }) => {
      const source = cardsStore.findByToken(token);
      if (!source) return;
      const copy = duplicateCardPayload(source);
      copy.section_token = anchorCard.section_token ?? null;
      cardsStore.addCard(copy, anchorToken);
      anchorToken = copy.token;
      lastCopyToken = copy.token;
    });

    updateLoopTokens(cards.value);
    if (lastCopyToken) selectCurrentCard({ token: lastCopyToken });
  }

  const updateCurrentCard = ({ index, card }) => {
    cards.value.splice(index, 1, card);
    cardsStore.updateCard(card.token);
  }

  const updateCardsBeforeSave = () => {
    const snapshots = new Map(cards.value.map((card) => [card.token, JSON.stringify(card.specs)]));
    updateAllDynamicAnswersAndItems(cards.value);
    cards.value.forEach((card) => {
      if (JSON.stringify(card.specs) !== snapshots.get(card.token)) cardsStore.updateCard(card.token);
    });
  }

  const applyDrag = () => {
    updateLoopTokens(cards.value);
    const token = currentCardStore.currentToken;
    if (!token) return;
    const index = cardsStore.findIndex(token);
    if (index !== -1 && index !== currentCardStore.currentIndex) selectCurrentCard({ token });
  }

  const findCardByToken = ({ token }) => cardsStore.findByToken(token) ?? undefined;
  const clearSearchQuery = () => (searchQuery.value = "");
  const updateSearchQuery = ({ value }) => (searchQuery.value = value);

  const redirectToEdit = ({ redirect, token = "" }) => {
    switchBetweenTabs.value = redirect;
    expandSectionIncludingCard(token);
  };

  const setActivePane = (pane) => {
    activePane.value = pane;
    if (pane === "Review" || pane === "Test") {
      switchBetweenTabs.value = true;
    }
  };

  const setStartFromCardToken = (token) => {
    startFromCardToken.value = token;
  };

  const clearStartFromCardToken = () => {
    startFromCardToken.value = "";
  };

  const isCardFilteredOut = (card) => {
    const { name = "", specs = {} } = card;
    const { question = {}, text = {} } = specs;

    // For questions we need to check the question and for other cards the text
    const fieldTocheck = card.type.startsWith("q_") ? question : text;

    return (
      !name.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
      !localesStore.localizeText(fieldTocheck)?.toLowerCase()?.includes(searchQuery.value.toLowerCase())
    );
  };

  return {
    cardTypeFor,
    recodePreset,
    // state
    cardSubTypesInfos,
    cardTypesGroups,
    searchQuery,
    switchBetweenTabs,
    firstLoadReviewPage,
    activePane,
    startFromCardToken,

    // actions
    addCard,
    addCardAtTheEnd,
    applyDrag,
    updateCardsBeforeSave,
    removeCard,
    duplicateCard,
    duplicateCards,
    selectCurrentCard,
    selectConditions,
    selectFirstCardAsCurrent,
    isSelectableCard,
    hasUnsavedChanges,
    refreshCards,
    updateCurrentCard,
    findCardByToken,
    isCardFilteredOut,
    redirectToEdit,
    updateSearchQuery,
    clearSearchQuery,
    setActivePane,
    setStartFromCardToken,
    clearStartFromCardToken,

    // getters
    isFilterActive,
    questionCount,
    cards,
    presenceOfCard,
  };
});
